import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowUturnLeftIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { openFile } from "../../utils/openFile";
import { Button, Card, ConfirmModal, EmptyState, Loading, Modal, Tag } from "../ui";
import { abroadReviewApi } from "../../api/abroadLoans";
import { errorMessageOf } from "../../api/scholarships";
import { canManageScholarships } from "../scholarships/access";
import { fullName } from "../scholarships/model";
import { evaluationStateOf, formatDateTime, formatScore } from "../scholarships/review/model";
import EligibilityFlag from "../scholarships/review/EligibilityFlag";
import { ApplicationDetails } from "./ApplicationSummary";
import { programmeLabel } from "./model";
import { REVIEW_KEY, applicationStateOf, useCommittee, useScoredApplication } from "./review";
import { rulesSummary } from "./student";

// One evaluator's marks per criterion, with their comments
function EvaluationCard({ evaluation, name, criteria, onReopen }) {
  const state = evaluationStateOf(evaluation.state);
  const markOf = (criterionId) => evaluation.marks.find((m) => m.criterionId === criterionId);
  return (
    <Card
      title={
        <span className="flex flex-wrap items-center gap-2">
          {name}
          <Tag color={state.color}>{state.label}</Tag>
        </span>
      }
      actions={
        onReopen && (
          <Button size="sm" variant="outline" icon={<ArrowUturnLeftIcon className="h-4 w-4" />} onClick={onReopen}>
            Reopen
          </Button>
        )
      }
    >
      <ul className="flex flex-col gap-3 text-sm">
        {criteria.map((c) => {
          const mark = markOf(c.id);
          return (
            <li key={c.id}>
              <div className="flex justify-between gap-3">
                <span className="text-slate-700">
                  {c.name} <span className="text-slate-400">· weight {c.weight}</span>
                </span>
                <span className="font-semibold text-slate-900">{mark?.marks == null ? "—" : `${formatScore(mark.marks)} / 100`}</span>
              </div>
              {mark?.comment && <p className="mt-1 whitespace-pre-line text-slate-500">{mark.comment}</p>}
            </li>
          );
        })}
      </ul>
      <p className="mt-4 border-t border-slate-100 pt-3 text-sm text-slate-700">
        Weighted total: <span className="font-semibold text-slate-900">{formatScore(evaluation.weightedTotal)}</span>
        {evaluation.submittedAt && <span className="text-slate-500"> · submitted {formatDateTime(evaluation.submittedAt)}</span>}
      </p>
    </Card>
  );
}

/**
 * One submitted application as staff read it: what the student sent, beside every evaluator's marks
 * and the conflicts declared. The HEC Admin may reopen a submitted evaluation while the application
 * is still undecided, so its evaluator can change it.
 */
export default function ScoredApplicationDialog({ call, row, onClose }) {
  const queryClient = useQueryClient();
  const { data, isPending, isError, refetch } = useScoredApplication(row?.applicationId);
  const { data: members = [] } = useCommittee(call.id);
  const [reopening, setReopening] = useState(null);
  const [busy, setBusy] = useState(false);

  const nameOf = (evaluatorId) => {
    const member = members.find((m) => m.evaluatorId === evaluatorId);
    return member ? fullName(member) : "Former committee member";
  };
  const application = data?.application;
  const offered = application && call.programmes.find((p) => p.programme.id === application.programme.id);
  const unmet = application?.unmetEligibility ?? [];
  const unmetRules = offered ? rulesSummary(offered.eligibility.filter((r) => unmet.includes(r.kind))) : [];
  const canReopen = canManageScholarships() && row?.state === "SUBMITTED";

  const open = (load, fileName) => openFile(async () => (await load()).data, fileName);

  const reopen = async () => {
    setBusy(true);
    try {
      await abroadReviewApi.reopen(row.applicationId, reopening.id);
      toast.success(`${nameOf(reopening.evaluatorId)} can change their marks again`);
      setReopening(null);
      await queryClient.invalidateQueries({ queryKey: REVIEW_KEY });
    } catch (error) {
      console.error(`Failed to reopen evaluation ${reopening.id} of abroad application ${row.applicationId}:`, error);
      toast.error(errorMessageOf(error, "Failed to reopen the evaluation. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const state = row && applicationStateOf(row.state);
  const conflicts = (data?.declarations ?? []).filter((d) => d.hasConflict);

  return (
    <Modal
      open={Boolean(row)}
      onClose={onClose}
      size="full"
      title={
        row && (
          <span className="flex flex-wrap items-center gap-3">
            {row.applicantName}
            <Tag color={state.color}>{state.label}</Tag>
          </span>
        )
      }
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      {isPending ? (
        <Loading label="Loading the application…" />
      ) : isError ? (
        <EmptyState title="Could not load the application" onRefresh={refetch} />
      ) : (
        <div className="flex flex-col gap-6">
          <p className="text-sm text-slate-600">
            {programmeLabel(application.programme)} · submitted {formatDateTime(application.submittedAt)} · average score{" "}
            <span className="font-semibold text-slate-900">{formatScore(data.averageTotal)}</span>
          </p>
          <EligibilityFlag eligibility={unmet.length ? { eligible: false, unmetRules: unmetRules.map((label) => ({ kind: label, label })) } : null} />
          <div className="grid gap-6 lg:grid-cols-2">
            <ApplicationDetails
              application={application}
              requirements={offered?.requirements}
              onOpen={(d) => open(() => abroadReviewApi.document(application.id, d.id), d.fileName)}
              onOpenLegacy={(d) => open(() => abroadReviewApi.legacyDocument(application.id, d.id), d.fileName)}
            />
            <div className="flex flex-col gap-4">
              {data.evaluations.length === 0 && <p className="text-sm text-slate-500">No evaluator has marked it yet.</p>}
              {data.evaluations.map((e) => (
                <EvaluationCard
                  key={e.id}
                  evaluation={e}
                  name={nameOf(e.evaluatorId)}
                  criteria={data.criteria}
                  onReopen={canReopen && e.state === "SUBMITTED" ? () => setReopening(e) : null}
                />
              ))}
              {conflicts.length > 0 && (
                <Card title="Conflicts of interest">
                  <ul className="flex flex-col gap-2 text-sm">
                    {conflicts.map((d) => (
                      <li key={d.evaluatorId}>
                        <span className="font-medium text-slate-800">{nameOf(d.evaluatorId)}</span>
                        <span className="text-slate-500"> · {formatDateTime(d.declaredAt)}</span>
                        {d.note && <p className="text-slate-600">{d.note}</p>}
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        open={Boolean(reopening)}
        onClose={() => setReopening(null)}
        onConfirm={reopen}
        loading={busy}
        title={`Reopen ${reopening ? nameOf(reopening.evaluatorId) : ""}'s marks?`}
        description="Their evaluation goes back to a draft. The application is no longer fully marked until they submit it again."
        confirmLabel="Reopen"
      />
    </Modal>
  );
}
